<template>
  <div>
    <v-data-table
      :loading="loading"
      :headers="headersByType"
      :items="requestVacations"
      :server-items-length="meta.total"
      :footer-props="footerProps"
      :options="options"
      :page="page"
      :items-per-page="itemsPerPage"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :multi-sort="multiSort"
      v-on="listeners"
    >
      <template v-slot:item.full_name="{ item }">
        <span class="mr-2">{{ item.full_name }}</span>
        <router-link
          class="text-decoration-none"
          target="_blank"
          :to="{
            name: 'employees.show',
            params: { id: item.employee_id }
          }"
        >
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon small v-on="on">mdi-eye-outline</v-icon>
            </template>
            <span v-if="item.last_name">{{ item.last_name }}</span>
            <span v-else>{{ $t('ITEM.VIEW') }}</span>
          </v-tooltip>
        </router-link>
      </template>

      <template v-slot:item.internal_note="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <span
              v-bind="attrs"
              class="d-inline-block text-truncate"
              style="max-width: 200px;"
              v-on="on"
            >
              {{ item.internal_note }}
            </span>
          </template>
          <span style="max-width: 500px; float: left; word-wrap: break-word;">{{
            item.internal_note
          }}</span>
        </v-tooltip>
      </template>

      <template v-slot:item.regions="{ item }">
        <div v-if="item.regions.length < 4">
          <v-chip v-for="region in item.regions" :key="region.id" class="mr-1">
            {{ region.name }}
          </v-chip>
        </div>

        <v-tooltip v-else top>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-chip
                v-for="region in item.regions.slice(0, 3)"
                :key="region.id"
                class="mr-1"
              >
                {{ region.name }}
              </v-chip>
              <span>... +{{ item.regions.length - 3 }} </span>
            </span>
          </template>
          <span style="max-width: 500px; float: left; word-wrap: break-word;">
            <v-chip
              v-for="region in item.regions"
              :key="region.id"
              class="ma-1"
            >
              {{ region.name }}
            </v-chip></span
          >
        </v-tooltip>
      </template>

      <template v-slot:item.date_from="{ item }">
        {{ item.date_from | dateFormat }}
      </template>

      <template v-slot:item.date_to="{ item }">
        {{ item.date_to | dateFormat }}
      </template>

      <template v-slot:item.created_at="{ item }">
        {{ item.created_at | dateFormat }}
      </template>

      <template v-slot:item.status_id="{ item }">
        <div v-if="!isEmptyObject(item.status)">
          <v-chip
            v-if="item.status.color"
            :color="item.status.color"
            text-color="white"
            small
          >
            {{ item.status.name }}
          </v-chip>
          <div v-else>
            {{ item.status.name }}
          </div>
        </div>
        <div v-else>
          {{ item.status }}
        </div>
      </template>

      <template v-slot:item.action="{ item }">
        <div class="text-center">
          <v-list-item @click="$emit('show', item)">
            <v-list-item-title>
              <v-icon class="mr-1" small>mdi-eye</v-icon>
              {{ $t('ITEM.VIEW') }}
            </v-list-item-title>
          </v-list-item>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { isEmpty, isEqual } from 'lodash';
import { dateFormat } from '@/modules/_global/filters';

export const ALL = 'all';
export const PERSON = 'person';

export const types = Object.freeze({
  ALL,
  PERSON
});

export default {
  name: 'RequestVacationsList',

  filters: {
    dateFormat
  },

  props: {
    requestVacations: {
      type: Array,
      required: true
    },
    meta: {
      type: Object,
      default: () => ({
        total: -1
      })
    },
    options: {
      type: Object,
      default: () => {}
    },
    type: {
      type: String,
      default: ALL,
      validator: prop => Object.values(types).includes(prop)
    },
    page: {
      type: Number,
      default: 1
    },
    itemsPerPage: {
      type: Number,
      default: 10
    },
    sortBy: {
      type: [String, Array],
      default: 'id'
    },
    sortDesc: {
      type: [Boolean, Array],
      default: false
    },
    multiSort: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      footerProps: {
        showFirstLastPage: true,
        itemsPerPageOptions: [10, 50, 100]
      },
      columnsByType: {
        [PERSON]: [3, 4, 5, 6, 7, 8, 9, 10],
        [ALL]: [0, 1, 2, 3, 4, 5, 6, 8, 9, 10]
      }
    };
  },

  computed: {
    listeners: function() {
      return Object.assign({}, this.$listeners, {
        'update:options': this.updateOptions
      });
    },

    headers() {
      return [
        {
          text: this.$t('ITEM.ID'),
          align: 'left',
          sortable: true,
          value: 'id'
        },
        {
          text: this.$t('EMPLOYEES.ITEM'),
          value: 'full_name'
        },
        {
          text: this.$t('REGIONS.ITEMS'),
          value: 'regions',
          sortable: false
        },
        {
          text: this.$t('ITEM.DATE_FROM'),
          value: 'date_from'
        },
        {
          text: this.$t('ITEM.DATE_TO'),
          value: 'date_to'
        },
        {
          text: this.$t('REQUEST_VACATIONS.QUANTITY_DAYS'),
          value: 'quantity_days'
        },
        {
          text: this.$t('REQUEST_VACATIONS.DATE_OF_REQUEST'),
          value: 'created_at'
        },
        {
          text: this.$t('REQUEST_VACATIONS.INTERNAL_NOTE'),
          value: 'internal_note'
        },
        {
          text: this.$t('ITEM.STATUS'),
          value: 'status_id'
        },
        {
          text: this.$t('DATA_TABLE.ACTIONS'),
          value: 'action',
          sortable: false,
          align: this.$can('sensitive-data') ? 'center' : ' d-none'
        }
      ];
    },

    columns() {
      return this.columnsByType[this.type];
    },

    headersByType() {
      return this.columns.map(i => this.headers[i]).filter(Boolean);
    }
  },

  methods: {
    updateOptions(options) {
      if (isEqual(options, this.options)) {
        return;
      }

      this.$emit('update:options', options);
    },

    isEmptyObject(v) {
      return isEmpty(v);
    }
  }
};
</script>
